import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import Input from '@wtag/rcl-input';
import Icon from '@wtag/rcl-icon';
import { LabelHint } from '@wtag/react-comp-lib';
import IconButton from '@wtag/rcl-icon-button';
import Amount from 'sharedWebpack/Amount';
import AddNewFee from './AddNewFee';
import feeList from '../../lib/feeList';
import './style.scss';

class FeesAndServices extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      requestingForFees: [],
    };
  }

  componentDidMount() {
    if (!this.props.fees.length) {
      this.props.fetchFees(this.context.callbackParams);
    }
    if (!this.props.feeSuggestions.length) {
      this.props.fetchFeeSuggestions(this.context.callbackParams);
    }
  }

  setSelectedFee = id => {
    const requestingForFees = new Set([...this.state.requestingForFees]);
    requestingForFees.add(id);
    this.setState({ requestingForFees: [...requestingForFees] });
  };

  deleteSelectedFee = id => {
    const requestingForFees = new Set([...this.state.requestingForFees]);
    requestingForFees.delete(id);
    this.setState({ requestingForFees: [...requestingForFees] });
  };

  feeSelected = id => this.props.selected.findIndex(selectedFee => selectedFee.id === id) > -1;

  selectFee = fee => {
    const { id } = fee;

    this.setSelectedFee(id);

    if (this.feeSelected(id)) {
      this.props.onRemove(id).then(() => this.deleteSelectedFee(id));
    } else {
      this.props.onAdd(fee).then(() => this.deleteSelectedFee(id));
    }
  };

  feeTotal = (currency, grossTotal, grossVat) => {
    const grossAmount = (
      <span>
        <Amount currency={currency} value={grossTotal} />
      </span>
    );

    if (!grossVat) return grossAmount;

    const vat = <Amount currency={currency} value={grossVat} />;

    return (
      <LabelHint
        hint={
          <Fragment>
            {I18n.t('ibe.payment.including')} {vat} {I18n.t('ibe.payment.vat')}
          </Fragment>
        }
      >
        {grossAmount}
      </LabelHint>
    );
  };

  quantityWithField = (id, quantity, disable) => (
    <Input
      type="number"
      size="tiny"
      value={quantity}
      isClearable={false}
      onChange={value => {
        if (!this.context.laymanMode) {
          this.props.setQuantity(id, value);
        }
      }}
      disabled={this.context.laymanMode || disable}
    />
  );

  allAvailableFees = () => {
    const fees = feeList(
      this.props.fees,
      this.props.selected,
      this.props.intl,
      !this.context.laymanMode,
    );

    const mappedFees = fees.map(({ id, name, grossTotal, grossVat, currency, quantity }) => (
      <div key={id} className="payment__fee-service d-flex align-center justify-between">
        <div className="payment__fee-service-fee-name">{name}</div>
        <div className="payment__fee-service-fee-quantity">
          {this.quantityWithField(id, quantity, false)}
        </div>
        <div className="payment__fee-service-value">
          {this.feeTotal(currency, grossTotal, grossVat)}
        </div>
        {!this.context.laymanMode && (
          <IconButton icon={<Icon name="close" />} onClick={() => this.props.onRemove(id)} />
        )}
      </div>
    ));

    if (!this.props.paymentMethodSurcharge?.title) {
      return mappedFees;
    }

    const paymentMethodSurcharge = (
      <div key={this.props.paymentMethodSurcharge.title}>
        <div className=" payment__fee-service-fee-name">
          {this.props.paymentMethodSurcharge.title}
        </div>
        <div>
          {this.quantityWithField(
            null,
            1,

            true,
          )}
        </div>
        <Amount
          className="payment__fee-service-value"
          currency={this.props.paymentMethodSurcharge.currency}
          value={this.props.paymentMethodSurcharge.amount}
        />
      </div>
    );

    return [...mappedFees, paymentMethodSurcharge];
  };

  render() {
    const totalFees = this.props.totalFeesAndPaymentMethodSurcharges || '0.0';

    if (this.props.feeSuggestions.length === 0) return null;

    return (
      <div className="d-flex-column flex-gap-20 payment__fee-services-container">
        <div className="d-flex justify-between align-center">
          <div className="payment__fee-services-name">
            {I18n.t('ibe.payment.services_and_charges')}
          </div>
          <Amount
            className="payment__fee-services-price"
            currency={this.context.currency}
            value={totalFees}
          />
        </div>
        {this.allAvailableFees().length ? (
          <div className="d-flex-column flex-gap-20 payment__fee-services">
            {this.allAvailableFees()}
          </div>
        ) : (
          <div className="payment__fee-services-empty align-self-center">
            {I18n.t('ibe.payment.no_services_and_charges')}
          </div>
        )}
        {!this.context.laymanMode && (
          <AddNewFee
            feeSuggestions={this.props.feeSuggestions}
            selectedFees={this.props.selected}
            requestingForFees={this.state.requestingForFees}
            selectFee={this.selectFee}
          />
        )}
      </div>
    );
  }
}

FeesAndServices.defaultProps = {
  totalFeesAndPaymentMethodSurcharges: '',
  selected: [],
  paymentMethodSurcharge: null,
};

FeesAndServices.propTypes = {
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  fees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      currency: PropTypes.string.isRequired,
      grossPrice: PropTypes.string.isRequired,
    }),
  ).isRequired,
  feeSuggestions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      currency: PropTypes.string.isRequired,
      grossPrice: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selected: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
      currency: PropTypes.string,
      grossPrice: PropTypes.string.isRequired,
    }),
  ),
  fetchFees: PropTypes.func.isRequired,
  fetchFeeSuggestions: PropTypes.func.isRequired,
  setQuantity: PropTypes.func.isRequired,
  totalFeesAndPaymentMethodSurcharges: PropTypes.string,
  intl: intlShape.isRequired,
  paymentMethodSurcharge: PropTypes.shape({
    title: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
  }),
};

FeesAndServices.contextTypes = {
  laymanMode: PropTypes.bool.isRequired,
  callbackParams: PropTypes.shape({}).isRequired,
  currency: PropTypes.string,
};

export default injectIntl(FeesAndServices);
